import React, { useState, useEffect } from 'react';
import api from '../api';
import { PackagePlus, Search, Truck, Calendar, Hash, Trash2, ClipboardList, Edit2, Save, X } from 'lucide-react';
import BarcodeScanInput from '../components/BarcodeScanInput';

const ShipmentsReceived = () => {
  const [vendors, setVendors] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  // PO-based form
  const [poNumber, setPoNumber] = useState('');
  const [poData, setPoData] = useState(null);
  const [poError, setPoError] = useState('');
  const [poLoading, setPoLoading] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [shipmentDate, setShipmentDate] = useState(new Date().toISOString().slice(0, 10));
  const [shipmentNumber, setShipmentNumber] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [v, s] = await Promise.all([
          api.getVendors(),
          api.request('/api/shipments'),
        ]);
        setVendors(Array.isArray(v) ? v : []);
        setShipments(Array.isArray(s) ? s : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const lookupPO = async () => {
    if (!poNumber.trim()) return;
    setPoLoading(true);
    setPoError('');
    setPoData(null);
    setLineItems([]);
    try {
      const formatted = poNumber.trim().toUpperCase().startsWith('PO-')
        ? poNumber.trim().toUpperCase()
        : `PO-${poNumber.trim().padStart(6, '0')}`;
      const data = await api.request(`/api/purchase-orders/by-number/${encodeURIComponent(formatted)}`);
      setPoData(data);
      setSelectedVendor(data.vendor_id || '');
      // Map line items with editable quantities matching the PO
      setLineItems((data.line_items || []).map(li => ({
        variant_id: li.variant_id || '',
        variant_name: li.variant_name || '',
        sku: li.sku || '',
        quantity_ordered: li.quantity_ordered || 0,
        quantity_received_prev: li.quantity_received || 0,
        quantity: li.quantity_ordered - (li.quantity_received || 0),
        cost_price: li.unit_cost || 0,
        checked: true,
      })));
    } catch (_err) {
      setPoError(`PO "${poNumber.trim()}" not found`);
    } finally {
      setPoLoading(false);
    }
  };

  const toggleItem = (idx) => {
    setLineItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, checked: !item.checked, quantity: !item.checked ? (item.quantity_ordered - item.quantity_received_prev) : 0 } : item
    ));
  };

  const updateItemQty = (idx, val) => {
    const max = lineItems[idx].quantity_ordered - lineItems[idx].quantity_received_prev;
    const parsed = Math.min(Math.max(0, parseInt(val) || 0), max);
    setLineItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, quantity: parsed, checked: parsed > 0 } : item
    ));
  };

  const updateItemCost = (idx, val) => {
    setLineItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, cost_price: parseFloat(val) || 0 } : item
    ));
  };

  const removeItem = (idx) => {
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const itemsToReceive = lineItems.filter(li => li.checked && li.quantity > 0);
    if (itemsToReceive.length === 0) {
      alert('Select at least one item with quantity > 0');
      return;
    }
    if (!poData) {
      alert('Please look up a Purchase Order first');
      return;
    }

    setSaving(true);
    try {
      const vendor = vendors.find(v => v.id === selectedVendor);
      for (const item of itemsToReceive) {
        const res = await api.request('/api/shipments', {
          method: 'POST',
          body: JSON.stringify({
            variant_id: item.variant_id,
            product_name: item.variant_name,
            vendor_id: selectedVendor,
            vendor_name: vendor ? vendor.name : poData.vendor_name || '',
            quantity: item.quantity,
            cost_price: item.cost_price,
            shipment_date: shipmentDate,
            shipment_number: shipmentNumber ? parseInt(shipmentNumber) : undefined,
            sku: item.sku,
            po_number: poData.po_number,
          }),
        });
        // res.shipment_number is reserved for future "last shipment created" UI feedback
        void res;
      }

      // Also mark items as received on the PO
      if (poData.id) {
        const updatedPo = await api.request(`/api/purchase-orders/${poData.id}/receive`, {
          method: 'POST',
          body: JSON.stringify({
            items: itemsToReceive.map(li => ({
              variant_id: li.variant_id,
              quantity: li.quantity,
            })),
          }),
        });

        // Check for quantity mismatches and notify user
        const mismatches = [];
        for (const li of (updatedPo.line_items || [])) {
          const ordered = li.quantity_ordered || 0;
          const received = li.quantity_received || 0;
          if (received !== ordered) {
            mismatches.push({
              name: li.variant_name || li.sku || 'Unknown',
              ordered,
              received,
              diff: ordered - received,
            });
          }
        }
        if (mismatches.length > 0) {
          // Create backend notification for quantity mismatch
          try {
            await api.request('/api/notifications', {
              method: 'POST',
              body: JSON.stringify({
                type: 'shipment_mismatch',
                message: `PO ${poData.po_number}: Received quantities do not match ordered quantities. ${mismatches.map(m => `${m.name}: ordered ${m.ordered}, received ${m.received}`).join('; ')}`,
                po_number: poData.po_number,
              }),
            });
          } catch (e) { console.warn('Mismatch notification failed:', e); }

          const mismatchMsg = mismatches.map(m =>
            `  - ${m.name}: Ordered ${m.ordered}, Received ${m.received} (${m.diff > 0 ? m.diff + ' short' : Math.abs(m.diff) + ' over'})`
          ).join('\n');
          alert(`Warning: Shipment quantities do not match PO ${poData.po_number}:\n\n${mismatchMsg}\n\nThe PO has been marked as partially received.`);
        }
      }

      // Refresh
      const s = await api.request('/api/shipments');
      setShipments(Array.isArray(s) ? s : []);

      // Reset form
      setPoNumber('');
      setPoData(null);
      setLineItems([]);
      setSelectedVendor('');
      setShipmentNumber('');
      setShipmentDate(new Date().toISOString().slice(0, 10));

      alert(`Shipment received! ${itemsToReceive.length} item(s) from ${poData.po_number}`);
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditData({
      quantity_received: s.quantity_received || 0,
      cost_price: s.cost_price || 0,
      vendor_name: s.vendor_name || '',
      shipment_date: s.shipment_date ? s.shipment_date.slice(0, 10) : '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (id) => {
    try {
      await api.request(`/api/shipments/${id}`, {
        method: 'PUT',
        body: JSON.stringify(editData),
      });
      const s = await api.request('/api/shipments');
      setShipments(Array.isArray(s) ? s : []);
      setEditingId(null);
      setEditData({});
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const deleteShipment = async (id, productName) => {
    if (!window.confirm(`Delete shipment for "${productName}"? This will reverse the inventory adjustment.`)) return;
    try {
      await api.request(`/api/shipments/${id}`, { method: 'DELETE' });
      const s = await api.request('/api/shipments');
      setShipments(Array.isArray(s) ? s : []);
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const fmtCurrency = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <div className="space-y-6" data-testid="shipments-received-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PackagePlus className="h-6 w-6" /> Shipments Received
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Receive inventory shipments from Purchase Orders</p>
        </div>
      </div>

      {/* PO-Based Receiving Form */}
      <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg p-6 max-w-4xl" data-testid="shipment-form">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <ClipboardList className="h-5 w-5" /> Receive from Purchase Order
        </h3>

        {/* PO Lookup */}
        <div className="flex gap-3 items-end mb-5">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Purchase Order # *</label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Enter PO number (e.g. PO-000001 or 1)"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupPO(); } }}
                className="w-full pl-9 pr-4 py-2 border border-input rounded-lg text-sm"
                data-testid="po-number-input"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={lookupPO}
            disabled={poLoading || !poNumber.trim()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
            data-testid="po-lookup-btn"
          >
            <Search className="h-4 w-4" /> {poLoading ? 'Looking up...' : 'Look Up'}
          </button>
        </div>

        {poError && <p className="text-sm text-red-500 mb-4" data-testid="po-error">{poError}</p>}

        {/* PO Info Banner */}
        {poData && (
          <div className="bg-muted/30 border border-border rounded-lg p-4 mb-5" data-testid="po-info-banner">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-muted-foreground">PO #:</span> <strong>{poData.po_number}</strong></div>
              <div><span className="text-muted-foreground">Vendor:</span> <strong>{poData.vendor_name || '—'}</strong></div>
              <div><span className="text-muted-foreground">Status:</span> <strong className="capitalize">{poData.status}</strong></div>
              <div><span className="text-muted-foreground">Total:</span> <strong>{fmtCurrency(poData.total)}</strong></div>
            </div>
          </div>
        )}

        {/* Shipment Details */}
        {poData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <div>
                <label className="block text-sm font-medium mb-1">
                  <Truck className="inline h-4 w-4 mr-1" />Vendor
                </label>
                <select
                  value={selectedVendor}
                  onChange={e => setSelectedVendor(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-background"
                  data-testid="shipment-vendor-select"
                >
                  <option value="">Select vendor...</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  <Calendar className="inline h-4 w-4 mr-1" />Shipment Date
                </label>
                <input
                  type="date"
                  value={shipmentDate}
                  onChange={e => setShipmentDate(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg text-sm"
                  data-testid="shipment-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  <Hash className="inline h-4 w-4 mr-1" />Shipment #
                </label>
                <input
                  type="number"
                  min="1"
                  value={shipmentNumber}
                  onChange={e => setShipmentNumber(e.target.value)}
                  placeholder="Auto-generated"
                  className="w-full px-3 py-2 border border-input rounded-lg text-sm"
                  data-testid="shipment-number"
                />
              </div>
            </div>

            {/* Line Items Table */}
            {/* Barcode scan: each scan finds the matching PO line by SKU/barcode and bumps Receive Qty by 1 */}
            <div className="mb-3" data-testid="receive-barcode-scan-wrapper">
              <label className="block text-sm font-medium mb-1">Scan to receive (auto-increments Receive Qty by 1)</label>
              <BarcodeScanInput
                onScan={(product) => {
                  const code = (product?.sku || '').toLowerCase();
                  const bc = (product?.barcode || '').toLowerCase();
                  const idx = lineItems.findIndex(li => {
                    const liSku = (li.sku || '').toLowerCase();
                    return liSku && (liSku === code || liSku === bc);
                  });
                  if (idx === -1) {
                    alert(`SKU ${product?.sku || '?'} ("${product?.product_name || ''}") isn't on this PO. Add it to the PO first to receive it here.`);
                    return;
                  }
                  setLineItems(items => items.map((li, i) => i === idx ? { ...li, quantity_received: (Number(li.quantity_received) || 0) + 1 } : li));
                }}
                onMiss={(c) => alert(`Barcode "${c}" not found in product catalog.`)}
                placeholder="Scan box label or type SKU…"
              />
            </div>
            <div className="border border-border rounded-lg overflow-hidden mb-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-center px-3 py-2 w-10">Recv</th>
                    <th className="text-left px-3 py-2">Product</th>
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-center px-3 py-2">PO Qty</th>
                    <th className="text-center px-3 py-2">Already Recv'd</th>
                    <th className="text-center px-3 py-2">Receive Qty</th>
                    <th className="text-right px-3 py-2">Cost</th>
                    <th className="text-center px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 ? (
                    <tr><td colSpan="8" className="px-4 py-6 text-center text-muted-foreground">No items in this PO</td></tr>
                  ) : lineItems.map((item, idx) => (
                    <tr key={idx} className={`border-b ${item.checked ? 'bg-green-50/50' : ''}`} data-testid={`shipment-line-${idx}`}>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={!!item.checked}
                          onChange={() => toggleItem(idx)}
                          className="h-4 w-4 rounded border-input accent-green-600"
                          data-testid={`shipment-check-${idx}`} />
                      </td>
                      <td className="px-3 py-2 font-medium">{item.variant_name}</td>
                      <td className="px-3 py-2 text-muted-foreground font-mono">{item.sku}</td>
                      <td className="px-3 py-2 text-center">{item.quantity_ordered}</td>
                      <td className="px-3 py-2 text-center text-muted-foreground">{item.quantity_received_prev}</td>
                      <td className="px-3 py-2 text-center">
                        <input type="number" min="0"
                          max={item.quantity_ordered - item.quantity_received_prev}
                          value={item.quantity}
                          onChange={(e) => updateItemQty(idx, e.target.value)}
                          disabled={!item.checked}
                          className="w-20 px-2 py-1 border border-input rounded text-center text-sm disabled:opacity-40"
                          data-testid={`shipment-qty-${idx}`} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" step="0.01" min="0"
                          value={item.cost_price}
                          onChange={(e) => updateItemCost(idx, e.target.value)}
                          disabled={!item.checked}
                          className="w-24 px-2 py-1 border border-input rounded text-right text-sm disabled:opacity-40"
                          data-testid={`shipment-cost-${idx}`} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button type="button" onClick={() => removeItem(idx)}
                          className="p-1 hover:bg-red-50 rounded text-red-400"
                          title="Remove item"
                          data-testid={`shipment-remove-${idx}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={saving || !poData || lineItems.filter(li => li.checked && li.quantity > 0).length === 0}
          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm font-medium"
          data-testid="shipment-submit-btn"
        >
          {saving ? 'Receiving...' : `Receive ${lineItems.filter(li => li.checked && li.quantity > 0).length} Item(s)`}
        </button>
      </form>

      {/* Shipment History */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/30">
          <h3 className="text-sm font-semibold">Shipment History</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="shipment-history-table">
            <thead>
              <tr className="border-b">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Date</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Product</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Vendor</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Qty Received</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Cost Price</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Received By</th>
                <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : shipments.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">No shipments received yet</td></tr>
              ) : shipments.map(s => (
                <tr key={s.id} className="hover:bg-muted/20" data-testid={`history-row-${s.id}`}>
                  {editingId === s.id ? (
                    <>
                      <td className="px-4 py-2 text-sm">
                        <input type="date" value={editData.shipment_date}
                          onChange={e => setEditData({...editData, shipment_date: e.target.value})}
                          className="w-full px-2 py-1 border border-input rounded text-sm"
                          data-testid={`edit-date-${s.id}`} />
                      </td>
                      <td className="px-4 py-2 text-sm font-medium">{s.product_name || '—'}</td>
                      <td className="px-4 py-2 text-sm">
                        <input type="text" value={editData.vendor_name}
                          onChange={e => setEditData({...editData, vendor_name: e.target.value})}
                          className="w-full px-2 py-1 border border-input rounded text-sm"
                          data-testid={`edit-vendor-${s.id}`} />
                      </td>
                      <td className="px-4 py-2 text-sm text-right">
                        <input type="number" min="0" value={editData.quantity_received}
                          onChange={e => setEditData({...editData, quantity_received: parseInt(e.target.value) || 0})}
                          className="w-20 px-2 py-1 border border-input rounded text-center text-sm"
                          data-testid={`edit-qty-${s.id}`} />
                      </td>
                      <td className="px-4 py-2 text-sm text-right">
                        <input type="number" step="0.01" min="0" value={editData.cost_price}
                          onChange={e => setEditData({...editData, cost_price: parseFloat(e.target.value) || 0})}
                          className="w-24 px-2 py-1 border border-input rounded text-right text-sm"
                          data-testid={`edit-cost-${s.id}`} />
                      </td>
                      <td className="px-4 py-2 text-sm text-muted-foreground">{s.created_by || '—'}</td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => saveEdit(s.id)} className="p-1 hover:bg-green-50 rounded text-green-600" title="Save" data-testid={`save-btn-${s.id}`}>
                            <Save className="h-4 w-4" />
                          </button>
                          <button onClick={cancelEdit} className="p-1 hover:bg-muted rounded text-muted-foreground" title="Cancel" data-testid={`cancel-btn-${s.id}`}>
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2.5 text-sm">{s.shipment_date ? new Date(s.shipment_date).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-2.5 text-sm font-medium">{s.product_name || '—'}</td>
                      <td className="px-4 py-2.5 text-sm text-muted-foreground">{s.vendor_name || '—'}</td>
                      <td className="px-4 py-2.5 text-sm text-right tabular-nums">{s.quantity_received}</td>
                      <td className="px-4 py-2.5 text-sm text-right tabular-nums">${(s.cost_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-sm text-muted-foreground">{s.created_by || '—'}</td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => startEdit(s)} className="p-1 hover:bg-muted rounded text-muted-foreground" title="Edit" data-testid={`edit-btn-${s.id}`}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => deleteShipment(s.id, s.product_name)} className="p-1 hover:bg-red-50 rounded text-red-400" title="Delete" data-testid={`delete-btn-${s.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ShipmentsReceived;
